import React, {useLayoutEffect, useRef} from 'react';
import styled from 'styled-components';

const Text = styled.span<{$maxWidth?: string}>`
	display: inline-block;
	min-width: 0;
	max-width: ${p => p.$maxWidth ?? '100%'};
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	vertical-align: bottom;
`;

export interface VersionNameProps {
	name: string;
	maxWidth?: string;
	/**
	 * Called whenever the name starts or stops being cut off. The host owns the focusable element the name sits in
	 * (a tab, a header button), so it wraps that element in a Tooltip with the full name while this reports true.
	 * The name itself never takes a focus stop.
	 */
	onCutChange?: (cut: boolean) => void;
}

/** A version name on one line, cut off with an ellipsis when it doesn't fit. */
export const VersionName = ({name, maxWidth, onCutChange}: VersionNameProps) => {
	const ref = useRef<HTMLSpanElement>(null);
	const report = useRef(onCutChange);
	report.current = onCutChange;
	useLayoutEffect(() => {
		const el = ref.current;
		if (!el) {
			return undefined;
		}
		const measure = () => report.current?.(el.scrollWidth > el.clientWidth);
		measure();
		if (typeof ResizeObserver === 'undefined') {
			return undefined;
		}
		const observer = new ResizeObserver(measure);
		observer.observe(el);
		return () => observer.disconnect();
	}, [name, maxWidth]);
	return (
		<Text ref={ref} $maxWidth={maxWidth}>
			{name}
		</Text>
	);
};
